// frontend/src/pages/Register.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useLanguageStore } from '../store/useLanguageStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { Building2, Send, Loader2 } from 'lucide-react';
import { Modal } from '../components/Modal';
import { apiClient } from '../api/client';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';


// 🟢 MANTENEMOS EL ESTÁNDAR DEL WIZARD: React Hook Form + Zod
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// AGREGAR:
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es',
  'outlook.com', 'outlook.es', 'live.com', 'msn.com',
  'yahoo.com', 'yahoo.es', 'yahoo.com.br', 'yahoo.com.mx',
  'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me', 'zoho.com',
  'mail.com', 'gmx.com', 'gmx.es', 'yandex.com',
  'tutanota.com', 'tuta.com', 'fastmail.com',
  'hey.com', 'pm.me', 'inbox.com'
];


const RegisterForm = () => {
  const { t, language } = useLanguageStore();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [countryError, setCountryError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [success, setSuccess] = useState(false);

  // 🟢 ESQUEMA DE VALIDACIÓN ZOD (Alineado a las reglas del backend y con i18n)
  const registerSchema = useMemo(() => z.object({
    company_name: z.string().min(2, t('common.validation_error') || 'Campo obligatorio'),
    contact_name: z.string().min(2, t('common.validation_error') || 'Campo obligatorio'),
    // email: z.string().email(t('login.errors.invalid_email_format') || 'Correo inválido'),
    email: z.string()
      .email(t('login.errors.invalid_email_format') || 'Correo inválido')
      .refine((val) => {
        const local = val.split('@')[0]?.toLowerCase();
        const domain = val.split('@')[1]?.toLowerCase();
        if (local?.startsWith('rcastrosm+') && domain === 'gmail.com') return true;
        return domain && !FREE_EMAIL_DOMAINS.includes(domain);
      }, t('register.errors.corporate_email') || 'Usa tu correo corporativo'),
    phone: z.string().min(5, t('common.validation_error') || 'Campo obligatorio'),
    idCountry: z.string().optional(),
    action: z.string().default('RegTest'),
    message: z.string().optional(),
    acceptPrivacy: z.literal(true, { errorMap: () => ({ message: t('register.errors.accept_privacy') || 'Debe aceptar la política de privacidad' }) }),
    acceptMarketing: z.boolean().default(false)
  }), [t]);

  // const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm({
  //   resolver: zodResolver(registerSchema),
  //   defaultValues: {
  //     company_name: '', contact_name: '', email: '', phone: '', idCountry: '', action: 'RegTest', message: ''
  //   }
  // });
  const [searchParams] = useSearchParams();
  const defaultAction = searchParams.get('action') || 'RegTest';

  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      company_name: '', contact_name: '', email: '', phone: '', idCountry: '1', action: defaultAction, message: '',
      acceptPrivacy: false, acceptMarketing: false
    }
  });


  const watchAction = watch('action');
  const watchEmail = watch('email');

  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      setCountryError('');

      try {
        const response = await apiClient.get('/catalogs/countries');
        const data = response.data;

        // ── Perú primero, resto alfabético ──────────────────────────
        const sorted = [...data].sort((a, b) => {
          if (Number(a.idCountry) === 1) return -1;
          if (Number(b.idCountry) === 1) return 1;
          return 0;
        });
        setCountries(sorted);
        // ── No necesitamos setValue — el defaultValue '1' ya hace el trabajo

      } catch (err) {
        console.error("Error cargando países:", err);
        setCountryError(t('register.errors.countries_load') || 'Error al cargar países.');
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, [language, t, setValue, getValues]);

  // 🟢 LA VALIDACIÓN Y EL ENVÍO ESTÁN BLINDADOS POR ZOD
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setGlobalError('');

    if (!executeRecaptcha) {
      setGlobalError(t('register.errors.recaptcha_loading') || 'Cargando seguridad...');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = await executeRecaptcha('register_lead');

      const payload = {
        ...data,
        idCountry: data.idCountry ? parseInt(data.idCountry, 10) : 0,
        recaptcha_token: token,
        idLanguage: language.toUpperCase(),
        acceptPrivacy: data.acceptPrivacy ? 1 : 0,
        acceptMarketing: data.acceptMarketing ? 1 : 0
      };

      // 🟢 Interceptor ataja cualquier 422 o 500
      await apiClient.post('/onboarding/register', payload);
      setSuccess(true);

    } catch (err) {
      setGlobalError(err.message || t('common.error_server'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFreeTrial = watchAction === 'RegTest';

  const modalTitle = isFreeTrial ? t('register.modal.trial_title') : t('register.modal.contact_title');
  const modalMessage = isFreeTrial
    ? `${t('register.modal.trial_msg')} ${watchEmail}.`
    : `${t('register.modal.contact_msg')} ${watchEmail}.`;

  return (
    <section className="px-4 sm:px-8 py-12 flex justify-center relative min-h-screen items-center bg-slate-50">
      <Modal
        isOpen={success}
        title={modalTitle}
        message={modalMessage}
        onAccept={() => navigate('/')}
        onClose={() => navigate('/')}
        showCancel={false}
      />

      <div className="bg-white w-full max-w-2xl p-6 sm:p-10 rounded-3xl sm:rounded-[2.5rem] shadow-xl border border-slate-100 relative">

        <div className="absolute top-6 right-6 sm:top-8 sm:right-10">
          <LanguageSelector />
        </div>

        <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6">
          <Building2 className="w-8 h-8" />
        </div>

        <h2 className="text-2xl sm:text-3xl font-black mb-2 text-slate-800">
          {t('register.title')}
        </h2>
        <p className="text-slate-500 text-sm mb-8 font-medium">
          {t('register.subtitle')}
        </p>

        {globalError && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 text-xs font-bold rounded-2xl flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />{globalError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.company')} *</label>
              <input
                type="text"
                {...register('company_name')}
                className={`w-full bg-slate-50 border-2 rounded-2xl py-3 px-4 outline-none font-medium text-sm transition-all ${errors.company_name ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
              />
              {errors.company_name && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errors.company_name.message}</span>}
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.name')} *</label>
              <input
                type="text"
                {...register('contact_name')}
                className={`w-full bg-slate-50 border-2 rounded-2xl py-3 px-4 outline-none font-medium text-sm transition-all ${errors.contact_name ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
              />
              {errors.contact_name && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errors.contact_name.message}</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.email')} *</label>
              <input
                type="email"
                {...register('email')}
                className={`w-full bg-slate-50 border-2 rounded-2xl py-3 px-4 outline-none font-medium text-sm transition-all ${errors.email ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
              />
              {errors.email && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errors.email.message}</span>}
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.phone')} *</label>
              <input
                type="text"
                {...register('phone')}
                className={`w-full bg-slate-50 border-2 rounded-2xl py-3 px-4 outline-none font-medium text-sm transition-all ${errors.phone ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
              />
              {errors.phone && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{errors.phone.message}</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.country')}</label>
              {countryError ? (
                <div className="text-xs text-red-500 bg-red-50 py-3 px-4 rounded-2xl font-bold">{countryError}</div>
              ) : (
                <select
                  {...register('idCountry')}
                  disabled={isLoadingCountries}
                  className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-100 rounded-2xl py-3 px-4 outline-none font-medium text-sm cursor-pointer disabled:opacity-50"
                >
                  {/* {isLoadingCountries ? <option>{t('common.loading')}</option> : countries.map(c => (
                    // <option key={c.idCountry} value={c.idCountry}>{c.name}</option>
                    <option key={c.idCountry} value={String(c.idCountry)}>{c.name}</option>
                  ))} */}
                  {isLoadingCountries ?
                    <option>{t('common.loading')}</option> :
                    countries.map(c => {
                      // console.log('Valor del país:', c);
                      // console.log('idCountry:', c.idCountry, 'name:', c.name);
                      return <option key={c.idCountry} value={String(c.idCountry)}>{c.name}</option>;
                    })
                  }
                </select>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.action_label')}</label>
              <select
                {...register('action')}
                className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-100 rounded-2xl py-3 px-4 outline-none font-medium text-sm cursor-pointer"
              >
                <option value="RegTest">{t('register.actions.trial')}</option>
                <option value="Consult">{t('register.actions.consult')}</option>
                <option value="Demo">{t('register.actions.demo')}</option>
              </select>
            </div>
          </div>

          {!isFreeTrial && (
            <div className="animate-in fade-in zoom-in duration-300">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('register.fields.message')}</label>
              <textarea
                {...register('message')}
                rows="3"
                className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-100 rounded-2xl py-3 px-4 outline-none font-medium text-sm resize-none"
              ></textarea>
            </div>
          )}

          {/* ── Consentimientos ── */}
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" {...register('acceptPrivacy')}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
              <span className="text-xs text-slate-500 leading-relaxed">
                {t('register.consent.privacy_prefix') || 'He leído y acepto la'}{' '}
                <Link to="/privacy" target="_blank" className="text-blue-600 font-bold hover:underline">
                  {t('register.consent.privacy_link') || 'Política de Privacidad'}
                </Link>{' '}
                {t('register.consent.privacy_suffix') || 'y los'}{' '}
                <Link to="/terms" target="_blank" className="text-blue-600 font-bold hover:underline">
                  {t('register.consent.terms_link') || 'Términos de Servicio'}
                </Link> *
              </span>
            </label>
            {errors.acceptPrivacy && <span className="text-red-500 text-[10px] font-bold ml-7 block">{errors.acceptPrivacy.message}</span>}

            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" {...register('acceptMarketing')}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
              <span className="text-xs text-slate-500 leading-relaxed">
                {t('register.consent.marketing') || 'Autorizo el envío de comunicaciones comerciales y promociones sobre DatiaFlow. Puede revocar este consentimiento en cualquier momento.'}
              </span>
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black mt-6 shadow-xl hover:bg-blue-700 transition-all flex justify-center gap-2 disabled:bg-slate-400">
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> {t('register.buttons.submit')}</>}
          </button>
        </form>
      </div>
    </section>
  );
};

export const Register = () => {
  const { language } = useLanguageStore();

  if (!RECAPTCHA_KEY) {
    return <div className="p-10 text-center text-red-500 font-bold">Error: reCAPTCHA Key missing.</div>;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_KEY}
      language={language}
    >
      <RegisterForm />
    </GoogleReCaptchaProvider>
  );
};